import React, { createContext, useContext, useEffect, useMemo, useRef } from 'react';

type BeforeNavigateHandler = (navigateFn: () => void) => void;

type OverlayNavContextProps = {
  entityIds: (string | number)[];
  basePath: string;
  beforeNavigateRef: React.MutableRefObject<BeforeNavigateHandler | null>;
};

const OverlayNavContext = createContext<OverlayNavContextProps | null>(null);

type OverlayNavProviderProps = {
  entityIds: (string | number)[];
  basePath: string;
  children: React.ReactNode;
};

export const OverlayNavProvider = ({ entityIds, basePath, children }: OverlayNavProviderProps) => {
  const beforeNavigateRef = useRef<BeforeNavigateHandler | null>(null);
  const value = useMemo(() => ({ entityIds, basePath, beforeNavigateRef }), [entityIds, basePath]);
  return <OverlayNavContext.Provider value={value}>{children}</OverlayNavContext.Provider>;
};

export const useOverlayNav = () => useContext(OverlayNavContext);

export const useOverlayNavGuard = (handler: BeforeNavigateHandler | null) => {
  const nav = useOverlayNav();
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!nav) return undefined;
    if (handler) {
      nav.beforeNavigateRef.current = (fn: () => void) => handlerRef.current?.(fn);
    } else {
      nav.beforeNavigateRef.current = null;
    }
    return () => {
      if (nav) nav.beforeNavigateRef.current = null;
    };
  }, [nav, !!handler]);
};
